import { DashboardPeriod, PERIOD_PREVIOUS_LABEL } from "../../../utils/dashboardPeriod";
import { numberWithCommas } from "../../../utils";
import Paper from "@mui/material/Paper";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Skeleton from "@mui/material/Skeleton";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import ChangeBadge from "./ChangeBadge";
import { COMPACT_QUERY } from "../../../utils/hooks/useCompact";

const compact = "@media " + COMPACT_QUERY;

interface WasteLeftoverSummaryProps {
  wasteQty: number;
  wastePercentDiff: number;
  leftoverQty: number;
  leftoverPercentDiff: number;
  loading: boolean;
  error: boolean;
  period: DashboardPeriod;
}

export default function WasteLeftoverSummary({
  wasteQty,
  wastePercentDiff,
  leftoverQty,
  leftoverPercentDiff,
  loading,
  error,
  period,
}: WasteLeftoverSummaryProps) {
  const prevLabel = PERIOD_PREVIOUS_LABEL[period];
  const total = wasteQty + leftoverQty;

  return (
    <Paper sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <Typography variant="body1" fontWeight={600} mb={2}>
        ยอดของเหลือ/ของเสีย
      </Typography>

      {loading && (
        <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 2 }}>
          {Array.from({ length: 3 }).map((_, index) => (
            <Skeleton
              key={index}
              variant="rectangular"
              height={84}
              sx={{ borderRadius: 3 }}
            />
          ))}
        </Box>
      )}

      {!loading && error && (
        <Box
          sx={{
            flex: 1,
            minHeight: 200,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Typography color="error" fontWeight={500}>
            ไม่สามารถโหลดข้อมูลได้
          </Typography>
        </Box>
      )}

      {!loading && !error && (
        <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 2 }}>
          <Box
            sx={{
              flex: 1,
              minHeight: 84,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              bgcolor: (theme) => theme.palette.error.main + "12",
              border: (theme) => `1px solid ${theme.palette.error.main}40`,
              borderRadius: 3,
              p: 2,
              [compact]: { p: 1.5 },
            }}
          >
            <Box display="flex" alignItems="center" gap={1.5}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 2,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "error.main",
                  bgcolor: (theme) => theme.palette.error.main + "1A",
                }}
              >
                <DeleteOutlineOutlinedIcon />
              </Box>
              <Box>
                <Typography color="text.secondary" variant="body2">
                  ยอดของเสีย (ชิ้น)
                </Typography>
                <Typography variant="h5" fontWeight={700}>
                  {numberWithCommas(wasteQty)}
                </Typography>
              </Box>
            </Box>
            <ChangeBadge percent={wastePercentDiff} label={prevLabel} tone="error" />
          </Box>

          <Box
            sx={{
              flex: 1,
              minHeight: 84,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              bgcolor: (theme) => theme.palette.warning.main + "12",
              border: (theme) => `1px solid ${theme.palette.warning.main}40`,
              borderRadius: 3,
              p: 2,
              [compact]: { p: 1.5 },
            }}
          >
            <Box display="flex" alignItems="center" gap={1.5}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 2,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "warning.main",
                  bgcolor: (theme) => theme.palette.warning.main + "1A",
                }}
              >
                <Inventory2OutlinedIcon />
              </Box>
              <Box>
                <Typography color="text.secondary" variant="body2">
                  ยอดของเหลือ (ชิ้น)
                </Typography>
                <Typography variant="h5" fontWeight={700}>
                  {numberWithCommas(leftoverQty)}
                </Typography>
              </Box>
            </Box>
            <ChangeBadge percent={leftoverPercentDiff} label={prevLabel} tone="warning" />
          </Box>

          {/* Total — a plain summary line (no icon, no secondary label),
              intentionally simpler than the waste/leftover rows (per design). */}
          <Box
            sx={{
              display: "flex",
              alignItems: "baseline",
              gap: 1,
              px: 2,
              [compact]: { px: 1.5 },
            }}
          >
            <Typography variant="body1" fontWeight={600}>
              รวม
            </Typography>
            <Typography variant="h6" fontWeight={700}>
              {numberWithCommas(total)}
            </Typography>
          </Box>
        </Box>
      )}
    </Paper>
  );
}
